import { isNullOrUndefined } from '@syncfusion/ej2-base';
import { AnnotationRenderer, FormFieldsBase } from './index';
import { PdfAnnotationFlag, PdfInkAnnotation, PdfPen, PdfPath } from '@syncfusion/ej2-pdf';
import { Rect } from '@syncfusion/ej2-drawings';
/**
 * SignatureBase
 *
 * @hidden
 */
export class SignatureBase {
    constructor(pdfViewer, pdfViewerBase) {
        this.pdfViewer = pdfViewer;
        this.pdfViewerBase = pdfViewerBase;
    }
    /**
     * @private
     * @param {string} jsonObject - jsonObject
     * @param {any} loadedDocument - loadedDocument
     * @returns {void}
     */
    saveSignatureData(jsonObject, loadedDocument) {
        const formfields = new FormFieldsBase(this.pdfViewer, this.pdfViewerBase);
        const signatureDetails = JSON.parse(jsonObject.signatureData);
        if (!isNullOrUndefined(signatureDetails)) {
            for (let i = 0; i < signatureDetails.length; i++) {
                const pageData = signatureDetails[parseInt(i.toString(), 10)];
                // Save signature as data
                if (pageData.length > 0) {
                    for (let p = 0; p < pageData.length; p++) {
                        const data = pageData[parseInt(p.toString(), 10)];
                        const signatureType = Object.prototype.hasOwnProperty.call(data, 'shapeAnnotationType') && data['shapeAnnotationType'] !== null
                            ? data['shapeAnnotationType'].toString()
                            : null;
                        if (signatureType !== null && signatureType === 'SignatureText') {
                            formfields.drawFreeTextAnnotations(data, loadedDocument, true);
                        }
                        else if (signatureType !== null && signatureType === 'SignatureImage') {
                            formfields.drawImage(data, loadedDocument, true);
                        }
                        else {
                            const pageNumber = data.pageIndex;
                            const page = loadedDocument.getPage(pageNumber);
                            const rotateAngle = this.getRotateAngle(page.rotation.toString());
                            const size = page.size;
                            let pageWidth = size[0];
                            let pageHeight = size[1];
                            if (rotateAngle === 1 || rotateAngle === 3) {
                                pageHeight = size[0];
                                pageWidth = size[1];
                            }
                            else {
                                pageHeight = size[1];
                                pageWidth = size[0];
                            }
                            let bounds = JSON.parse(data.bounds);
                            bounds = this.getSignatureBounds(bounds, this.convertPointToPixel(pageHeight), this.convertPointToPixel(pageWidth), rotateAngle);
                            const stampObjects = JSON.parse(data.data);
                            const left = this.convertPixelToPoint(bounds.left);
                            const top = this.convertPixelToPoint(bounds.top);
                            const width = this.convertPixelToPoint(bounds.width);
                            const height = this.convertPixelToPoint(bounds.height);
                            const opacity = data.opacity;
                            const thickness = data.thickness;
                            const strokeColor = JSON.parse(data.strokeColor);
                            const color = [strokeColor.r, strokeColor.g, strokeColor.b];
                            let minimumX = -1;
                            let minimumY = -1;
                            let maximumX = -1;
                            let maximumY = -1;
                            for (let p = 0; p < stampObjects.length; p++) {
                                const value = stampObjects[parseInt(p.toString(), 10)];
                                if (minimumX === -1) {
                                    minimumX = value.x;
                                    minimumY = value.y;
                                    maximumX = value.x;
                                    maximumY = value.x;
                                }
                                else {
                                    const point1 = value.x;
                                    const point2 = value.y;
                                    if (minimumX >= point1) {
                                        minimumX = point1;
                                    }
                                    if (minimumY >= point2) {
                                        minimumY = point2;
                                    }
                                    if (maximumX <= point1) {
                                        maximumX = point1;
                                    }
                                    if (maximumY <= point2) {
                                        maximumY = point2;
                                    }
                                }
                            }
                            const newDifferenceX = maximumX - minimumX;
                            const newDifferenceY = maximumY - minimumY;
                            let newPoint1 = [0, 0];
                            const loadedPage = loadedDocument.getPage(pageNumber);
                            let graphics = null;
                            if (loadedPage != null) {
                                graphics = loadedPage.graphics;
                                graphics.save();
                                graphics.setTransparency(opacity);
                                graphics.translateTransform(left, top);
                            }
                            const colors = new PdfPen(color, width);
                            colors._width = this.convertPixelToPoint(thickness);
                            if (stampObjects.length > 0) {
                                let dataPath = new PdfPath();
                                for (let j = 0; j < stampObjects.length; j++) {
                                    const value = stampObjects[parseInt(j.toString(), 10)];
                                    const path = value.command.toString();
                                    const differenceX = ((newDifferenceX) / width);
                                    const differenceY = ((newDifferenceY) / height);
                                    const newX = ((value.x - minimumX) / differenceX);
                                    const currentY = ((value.y - minimumY) / differenceY);
                                    if (path === 'M') {
                                        if (j !== 0) {
                                            page.graphics.drawPath(dataPath, colors, null);
                                            dataPath = new PdfPath();
                                        }
                                        newPoint1 = [newX, currentY];
                                        if (!isNullOrUndefined(graphics)) {
                                            dataPath.addLine(newX, currentY, newX, currentY);
                                        }
                                    }
                                    else if (path === 'L') {
                                        const newPoint2 = [newX, currentY];
                                        if (graphics != null) {
                                            // Removed this line to fix the issue EJ2-60295
                                            // graphics.DrawLine(colors, newpoint1, newpoint2);
                                            dataPath.addLine(newPoint1[0], newPoint1[1], newPoint2[0], newPoint2[1]);
                                        }
                                        newPoint1 = newPoint2;
                                    }
                                    if (j === stampObjects.length - 1) {
                                        page.graphics.drawPath(dataPath, colors, null);
                                    }
                                }
                            }
                            if (graphics != null) {
                                graphics.restore();
                            }
                        }
                    }
                }
            }
        }
    }
    /**
     * getSignatureBounds
     *
     * @param {Rect} bounds - bounds
     * @param {number} pageHeight - pageHeight
     * @param {number} pageWidth - pageWidth
     * @param {number} rotateAngle - rotateAngle
     * @returns {void}
     */
    getSignatureBounds(bounds, pageHeight, pageWidth, rotateAngle) {
        let bound;
        if (rotateAngle === 0) {
            bound = { 'left': bounds.left, 'top': bounds.top, 'width': bounds.width, 'height': bounds.height };
        }
        else if (rotateAngle === 1) {
            bound = { 'left': (pageWidth - bounds.top - bounds.height), 'top': bounds.left, 'width': bounds.height, 'height': bounds.width };
        }
        else if (rotateAngle === 2) {
            bound = { 'left': (pageWidth - bounds.left - bounds.width), 'top': (pageHeight - bounds.top - bounds.height), 'width': bounds.width, 'height': bounds.height };
        }
        else if (rotateAngle === 3) {
            bound = { 'left': bounds.top, 'top': (pageHeight - bounds.left - bounds.width), 'width': bounds.height, 'height': bounds.width };
        }
        return bound;
    }
    /**
     * @private
     * @param {string} jsonObject - jsonObject
     * @param {any} loadedDocument - loadedDocument
     * @returns {void}
     */
    saveSignatureAsAnnotatation(jsonObject, loadedDocument) {
        const annotationRenderer = new AnnotationRenderer(this.pdfViewer, this.pdfViewerBase);
        const signatureDetails = JSON.parse(jsonObject.signatureData);
        if (!isNullOrUndefined(signatureDetails)) {
            for (let i = 0; i < signatureDetails.length; i++) {
                const pageData = signatureDetails[parseInt(i.toString(), 10)];
                // Save signature as data
                if (pageData.length > 0) {
                    for (let p = 0; p < pageData.length; p++) {
                        const formfields = new FormFieldsBase(this.pdfViewer, this.pdfViewerBase);
                        const signatureAnnotation = pageData[parseInt(p.toString(), 10)];
                        const signatureType = Object.prototype.hasOwnProperty.call(signatureAnnotation, 'shapeAnnotationType') && signatureAnnotation['shapeAnnotationType'] !== null
                            ? signatureAnnotation['shapeAnnotationType'].toString()
                            : null;
                        if (signatureType !== null && signatureType === 'SignatureText') {
                            formfields.drawFreeTextAnnotations(signatureAnnotation, loadedDocument, false);
                        }
                        else if (signatureType !== null && signatureType === 'SignatureImage') {
                            formfields.drawImage(signatureAnnotation, loadedDocument, false);
                        }
                        else {
                            const bounds = JSON.parse(signatureAnnotation.bounds);
                            const stampObjects = JSON.parse(signatureAnnotation.data);
                            const pageNumber = signatureAnnotation.pageIndex;
                            const page = loadedDocument.getPage(pageNumber);
                            const cropValues = annotationRenderer.getCropBoxValue(page, false);
                            const left = cropValues.x + this.convertPixelToPoint(bounds.left);
                            let top = this.convertPixelToPoint(bounds.top);
                            if (!(cropValues.x === 0 && (page.cropBox[2] === page.size[0] && cropValues.y === page.size[1]))) {
                                top -= cropValues.y;
                            }
                            const width = this.convertPixelToPoint(bounds.width);
                            const height = this.convertPixelToPoint(bounds.height);
                            // let cropX = 0;
                            // let cropY = 0;
                            // if(page.cropBox.x)
                            const opacity = signatureAnnotation.opacity;
                            const thickness = signatureAnnotation.thickness;
                            const strokeColor = JSON.parse(signatureAnnotation.strokeColor);
                            const color = [strokeColor.r, strokeColor.g, strokeColor.b];
                            let minimumX = -1;
                            let minimumY = -1;
                            let maximumX = -1;
                            let maximumY = -1;
                            const rotationAngle = annotationRenderer.getInkRotateAngle(page.rotation.toString());
                            const drawingPath = new PdfPath();
                            for (let p = 0; p < stampObjects.length; p++) {
                                const val = stampObjects[parseInt(p.toString(), 10)];
                                drawingPath.addLine(val.x, val.y, 0, 0);
                            }
                            const rotatedPath = annotationRenderer.getRotatedPathForMinMax(drawingPath._points, rotationAngle);
                            for (let k = 0; k < rotatedPath.points.length; k += 2) {
                                const value = rotatedPath.points[parseInt(k.toString(), 10)];
                                if (minimumX === -1) {
                                    minimumX = value[0];
                                    minimumY = value[1];
                                    maximumX = value[0];
                                    maximumY = value[1];
                                }
                                else {
                                    const point1 = value[0];
                                    const point2 = value[1];
                                    if (minimumX >= point1) {
                                        minimumX = point1;
                                    }
                                    if (minimumY >= point2) {
                                        minimumY = point2;
                                    }
                                    if (maximumX <= point1) {
                                        maximumX = point1;
                                    }
                                    if (maximumY <= point2) {
                                        maximumY = point2;
                                    }
                                }
                            }
                            let newDifferenceX = (maximumX - minimumX) / width;
                            let newDifferenceY = (maximumY - minimumY) / height;
                            if (newDifferenceX === 0) {
                                newDifferenceX = 1;
                            }
                            else if (newDifferenceY === 0) {
                                newDifferenceY = 1;
                            }
                            let linePoints = [];
                            let isNewValues = 0;
                            if (rotationAngle !== 0) {
                                for (let j = 0; j < stampObjects.length; j++) {
                                    const val = stampObjects[parseInt(j.toString(), 10)];
                                    const path = val['command'].toString();
                                    if (path === 'M' && j !== isNewValues) {
                                        isNewValues = j;
                                        break;
                                    }
                                    linePoints.push((parseFloat(val['x'].toString())));
                                    linePoints.push((parseFloat(val['y'].toString())));
                                }
                                const rotatedPoints = annotationRenderer.getRotatedPath(linePoints, rotationAngle);
                                linePoints = [];
                                for (let z = 0; z < rotatedPoints._points.length; z += 2) {
                                    linePoints.push((rotatedPoints._points[parseInt(z.toString(), 10)][0] - minimumX)
                                        / newDifferenceX + left);
                                    linePoints.push(page.size[1] - (rotatedPoints._points[parseInt(z.toString(), 10)][1]
                                        - minimumY) / newDifferenceY - top);
                                }
                            }
                            else {
                                for (let j = 0; j < stampObjects.length; j++) {
                                    const val = stampObjects[parseInt(j.toString(), 10)];
                                    const path = val['command'].toString();
                                    if (path === 'M' && j !== isNewValues) {
                                        isNewValues = j;
                                        break;
                                    }
                                    linePoints.push(((val.x - minimumX) / newDifferenceX) + left);
                                    const newX = ((val.y - minimumY) / newDifferenceY);
                                    linePoints.push(page.size[1] - newX - top);
                                }
                            }
                            const inkAnnotation = new PdfInkAnnotation([left, top, width, height], linePoints);
                            let bound = new Rect();
                            bound = new Rect(inkAnnotation.bounds.x, (page.size[1] - (inkAnnotation.bounds.y +
                                inkAnnotation.bounds.height)), inkAnnotation.bounds.width, inkAnnotation.bounds.height);
                            inkAnnotation.bounds = bound;
                            inkAnnotation.color = color;
                            linePoints = [];
                            if (isNewValues > 0) {
                                if (rotationAngle !== 0) {
                                    const pathCollection = [];
                                    for (let i = isNewValues; i < stampObjects.length; i++) {
                                        const val = stampObjects[parseInt(i.toString(), 10)];
                                        const path = val['command'].toString();
                                        if (path === 'M' && i !== isNewValues) {
                                            pathCollection.push(linePoints);
                                            linePoints = [];
                                        }
                                        linePoints.push(val['x']);
                                        linePoints.push(val['y']);
                                    }
                                    if (linePoints.length > 0) {
                                        pathCollection.push(linePoints);
                                    }
                                    for (let g = 0; g < pathCollection.length; g++) {
                                        let graphicsPoints = [];
                                        const pointsCollections = pathCollection[parseInt(g.toString(), 10)];
                                        if (pointsCollections.length > 0) {
                                            const rotatedPoints = annotationRenderer.getRotatedPath(pointsCollections, rotationAngle);
                                            for (let z = 0; z < rotatedPoints._points.length; z += 2) {
                                                graphicsPoints.push((rotatedPoints._points[parseInt(z.toString(), 10)][0] - minimumX)
                                                    / newDifferenceX + left);
                                                graphicsPoints.push(page.size[1] - (rotatedPoints._points[parseInt(z.toString(), 10)][1]
                                                    - minimumY) / newDifferenceY - top);
                                            }
                                            inkAnnotation.inkPointsCollection.push(graphicsPoints);
                                        }
                                        graphicsPoints = [];
                                    }
                                }
                                else {
                                    for (let i = isNewValues; i < stampObjects.length; i++) {
                                        const val = stampObjects[parseInt(i.toString(), 10)];
                                        const path = val['command'].toString();
                                        if (path === 'M' && i !== isNewValues) {
                                            inkAnnotation.inkPointsCollection.push(linePoints);
                                            linePoints = [];
                                        }
                                        linePoints.push((val['x'] - minimumX) / newDifferenceX + left);
                                        const newX = ((val['y'] - minimumY) / newDifferenceY);
                                        linePoints.push(page.size[1] - newX - top);
                                    }
                                    if (linePoints.length > 0) {
                                        inkAnnotation.inkPointsCollection.push(linePoints);
                                    }
                                }
                            }
                            inkAnnotation.border.width = thickness;
                            inkAnnotation.opacity = opacity;
                            inkAnnotation._dictionary.set('NM', signatureAnnotation.signatureName.toString());
                            inkAnnotation._annotFlags = PdfAnnotationFlag.print;
                            if (Object.prototype.hasOwnProperty.call(signatureAnnotation, 'author') && signatureAnnotation['author'] !== null) {
                                const author = signatureAnnotation['author'].toString();
                                if (author !== 'Guest') {
                                    inkAnnotation.author = author;
                                }
                            }
                            page.annotations.add(inkAnnotation);
                        }
                    }
                }
            }
        }
    }
    convertPointToPixel(number) {
        return number * 96 / 72;
    }
    convertPixelToPoint(value) {
        return (value * (72 / 96));
    }
    getRotateAngle(angleString) {
        let angle = 0;
        switch (angleString) {
            case 'RotateAngle0':
            case '0':
                angle = 0;
                break;
            case 'RotateAngle180':
            case '2':
                angle = 2;
                break;
            case 'RotateAngle270':
            case '3':
                angle = 3;
                break;
            case 'RotateAngle90':
            case '1':
                angle = 1;
                break;
        }
        return angle;
    }
}
